import dayjs from "dayjs";
import { startOfPayWeek } from "../../../utils/payWeek";

// Weekday (Mon–Fri) hours up to this limit are regular for the Fri–Thu pay week.
export const WEEKLY_REGULAR_LIMIT = 40 * 60;

export const isWeekend = (date) => [0, 6].includes(dayjs(date).day());

export const formatMinutesToHM = (totalMinutes, empty = "—") => {
    const mins = Math.round(totalMinutes || 0);
    if (mins <= 0) return empty;
    const hours = Math.floor(mins / 60);
    const minutes = mins % 60;
    if (hours === 0) return `${minutes}m`;
    return `${hours}h ${String(minutes).padStart(2, "0")}m`;
}

// Position of a weekday inside the Fri–Thu pay week (Fri = 1 … Thu = 5).
export const workdayIndex = (date) => {
    let current = startOfPayWeek(date);
    const target = dayjs(date).startOf("day");
    let index = 0;
    while (!current.isAfter(target, "day")) {
        if (!isWeekend(current)) index += 1;
        current = current.add(1, "day");
    }
    return index;
}

// Minutes of an entry that count toward pay. A past day with no clock-out
// counts as 0, matching the backend (a missed clock-out isn't paid time).
export const paidMinutes = (entry) => {
    if (!entry?.checkin_time) return 0;
    if (!entry?.checkout_time && !dayjs(entry?.date).isSame(dayjs(), "day")) return 0;
    return Math.max(0, entry?.working_time || 0);
}

// Weekly pay for one employee row from the time-tracking list.
// - No daily OT: all weekday time is regular until the week's weekday total
//   passes 40h; the rest becomes weekly OT (lands on the last workdays).
// - Weekend (Sat/Sun) time is always OT.
// - No OT approval: all OT is paid at the employee's OT rate.
export const calcWeeklyPay = (employee) => {
    const entries = [...(employee?.timeTracking || [])].sort((a, b) => (a?.date > b?.date ? 1 : -1));

    const days = {};
    let weekdayMinutes = 0;
    let regularMinutes = 0;
    let weekendOtMinutes = 0;
    let weeklyOtMinutes = 0;

    entries.forEach(entry => {
        const worked = paidMinutes(entry);
        const wtdBefore = weekdayMinutes;

        if (isWeekend(entry?.date)) {
            weekendOtMinutes += worked;
            days[entry?.date] = { worked, reg: 0, ot: worked, isWeekend: true, wtdBefore, wtdAfter: weekdayMinutes };
            return;
        }

        const reg = Math.min(worked, Math.max(0, WEEKLY_REGULAR_LIMIT - weekdayMinutes));
        const ot = worked - reg;
        weekdayMinutes += worked;
        regularMinutes += reg;
        weeklyOtMinutes += ot;
        days[entry?.date] = { worked, reg, ot, isWeekend: false, wtdBefore, wtdAfter: weekdayMinutes };
    });

    const otMinutes = weekendOtMinutes + weeklyOtMinutes;
    const regularWage = employee?.wage > 0 ? (regularMinutes / 60) * employee.wage : 0;
    const otWage = employee?.ot_wage > 0 ? (otMinutes / 60) * employee.ot_wage : 0;

    return { days, weekdayMinutes, regularMinutes, otMinutes, weekendOtMinutes, weeklyOtMinutes, regularWage, otWage, wageTotal: regularWage + otWage };
}

// Wage for one day's regular/OT split.
export const calcDayWage = (employee, day) => {
    const reg = employee?.wage > 0 ? ((day?.reg || 0) / 60) * employee.wage : 0;
    const ot = employee?.ot_wage > 0 ? ((day?.ot || 0) / 60) * employee.ot_wage : 0;
    return { regularWage: reg, otWage: ot, wageTotal: reg + ot };
}
